"use client";

import { Link } from "@/components/CustomLink";
import { Button } from "@/components/ui/button";
import { ROUTES } from "@/@core/constants/routes";
import { Skeleton } from "@/components/ui/skeleton";
import S3Image from "@/components/S3Image";
import { useEffect, useState } from "react";
import VenueCardNoImg from "@/components/views/list/card/VenueCardNoImg";
import { cn } from "@/lib/utils";

interface BlogCardProps extends Pick<Blog, "id" | "descriptions"> {
  titleHeadingLevel: "h2" | "h3";
}

export default function BlogCard(props: BlogCardProps) {
  const [description] = props?.descriptions || [];
  const { id } = props;
  const { slug, featured_image, title, short_description } = description || {};

  const [imageError, setImageError] = useState(false);

  const getHeading = () => {
    const className = cn(
      "text-base font-bold text-ellipsis overflow-hidden w-full",
      short_description?.length > 0 ? "line-clamp-2" : "line-clamp-5",
    );

    if (props.titleHeadingLevel == "h2") {
      return <h2 className={cn(className)}>{description?.title}</h2>;
    }

    if (props.titleHeadingLevel == "h3") {
      return <h3 className={cn(className)}>{description?.title}</h3>;
    }
  };

  return (
    <article className="w-full lg:max-w-[386px] md:min-w-[386px]">
      <Link
        title={description?.title}
        scroll={true}
        href={`${ROUTES.BLOG}/${slug}`}
        className="p-4 !w-full lg:max-w-[386px] md:min-w-[386px] border border-gray-200 flex items-center gap-4 rounded-[24px]"
      >
        {!featured_image || imageError ? (
          <div className="w-[161px] h-[121px]">
            <VenueCardNoImg type={0} />
          </div>
        ) : (
          <S3Image
            sizes="161px"
            width={161}
            height={121}
            className="w-[161px] h-[121px] rounded-2xl"
            src={`/image/blog/${props.id}/${featured_image}?w=200&quality=60&format=webp&fit=contain`}
            alt={title}
            title={title}
            unoptimized
            onError={() => setImageError(true)}
            style={{
              maxWidth: "100%",
            }}
          />
        )}

        <div className="flex flex-col gap-1 h-[121px] flex-1">
          {getHeading()}

          {short_description && (
            <div>
              <span className="text-xs line-clamp-4 overflow-hidden">
                {short_description}
              </span>
            </div>
          )}
        </div>
      </Link>
    </article>
  );
}

export function BlogSkeleton() {
  return (
    <div
      className={`p-4 max-md:w-full lg:max-w-[386px] border border-gray-200 flex md:justify-between items-center gap-4 rounded-[24px]`}
    >
      <Skeleton className="w-[161px] h-[121px] rounded-2xl custom-animate-pulse" />

      <div className="flex flex-col justify-between h-[121px] md:w-[176px]">
        <Skeleton className="w-32 h-6 custom-animate-pulse" />

        <Skeleton className="w-40 h-6 custom-animate-pulse" />
      </div>
    </div>
  );
}
